<template>
  <div class="clerk-list-container">
    <div class="page-header"><h2><span class="breadcrumb-level">系统管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">账号权限</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">店员配置</span></h2></div>
    <el-card shadow="hover">
      <div class="card-header">
        <div class="filter-group">
          <el-input
            v-model="searchQuery"
            placeholder="搜索店员姓名、微信名或手机号"
            style="width: 320px; margin-right: 10px;"
            clearable
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" @click="handleSearch">
            <el-icon><search /></el-icon> 搜索
          </el-button>
        </div>
        <el-button type="primary" @click="openAddDialog">
          <el-icon><plus /></el-icon> 添加店员
        </el-button>
      </div>

      <el-table :data="clerks" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="用户名" />
        <el-table-column prop="wechat_name" label="微信名" />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="real_name" label="真实姓名" />
        <el-table-column label="权限" width="80">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="openPermissionDialog(row)">编辑</el-button>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="注册时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right">
          <template #default="{ row }">
            <el-popconfirm
              title="确定移除该店员标识吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="handleRemove(row)"
            >
              <template #reference>
                <el-button type="danger" size="small">
                  <el-icon><remove /></el-icon> 移除店员
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 添加店员对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="选择用户添加为店员"
      width="700px"
      :close-on-click-modal="false"
    >
      <div class="dialog-search">
        <el-input
          v-model="userSearchQuery"
          placeholder="搜索用户名、微信名或手机号"
          style="width: 300px; margin-right: 10px;"
          clearable
          @keyup.enter="searchUsers"
        >
          <template #prefix>
            <el-icon><search /></el-icon>
          </template>
        </el-input>
        <el-button type="primary" @click="searchUsers">搜索</el-button>
      </div>

      <el-table
        :data="availableUsers"
        border
        stripe
        style="width: 100%; margin-top: 15px;"
        v-loading="userSearchLoading"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name" label="用户名" />
        <el-table-column prop="wechat_name" label="微信名" />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column prop="real_name" label="真实姓名" />
      </el-table>

      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :disabled="selectedUserIds.length === 0"
          @click="confirmAdd"
        >
          确认为店员 ({{ selectedUserIds.length }})
        </el-button>
      </template>
    </el-dialog>

    <!-- 权限编辑对话框 -->
    <el-dialog
      v-model="permDialogVisible"
      title="编辑店员权限"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-checkbox-group v-model="permSelected">
        <div class="perm-section">
          <div class="perm-section-title">查看权限</div>
          <el-checkbox label="hotel_view">酒店订单查看</el-checkbox>
          <el-checkbox label="mall_view">商城订单查看</el-checkbox>
          <el-checkbox label="park_view">乐园订单查看</el-checkbox>
          <el-checkbox label="daycare_view">日托订单查看</el-checkbox>
          <el-checkbox label="activity_view">活动报名查看</el-checkbox>
        </div>
        <div class="perm-section">
          <div class="perm-section-title">核销权限</div>
          <el-checkbox label="hotel_verify">酒店订单核销</el-checkbox>
          <el-checkbox label="mall_verify">商城订单核销</el-checkbox>
          <el-checkbox label="park_verify">乐园订单核销</el-checkbox>
          <el-checkbox label="daycare_verify">日托订单核销</el-checkbox>
          <el-checkbox label="activity_verify">活动报名核销</el-checkbox>
        </div>
      </el-checkbox-group>
      <template #footer>
        <el-button @click="permDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPermission">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Plus, Remove, Edit } from '@element-plus/icons-vue';
import { fetchClerks, addClerk, batchAddClerks, removeClerk, fetchAvailableUsers, updateClerkPermissions } from '../../api/clerk';

export default {
  name: 'ClerkList',
  components: { Search, Plus, Remove, Edit },
  setup() {
    const clerks = ref([]);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);

    const addDialogVisible = ref(false);
    const userSearchQuery = ref('');
    const availableUsers = ref([]);
    const userSearchLoading = ref(false);
    const selectedUserIds = ref([]);

    const permDialogVisible = ref(false);
    const permTargetUser = ref(null);
    const permSelected = ref([]);

    const fetchData = async () => {
      loading.value = true;
      try {
        const response = await fetchClerks({
          page: currentPage.value,
          per_page: pageSize.value,
          search: searchQuery.value,
          sort_by: 'id',
          sort_order: 'desc'
        });
        clerks.value = Array.isArray(response.data) ? response.data : [];
        total.value = response.pagination?.total || response.total || 0;
      } catch (error) {
        ElMessage.error('获取店员列表失败');
        clerks.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const searchUsers = async () => {
      userSearchLoading.value = true;
      try {
        const response = await fetchAvailableUsers({ search: userSearchQuery.value });
        availableUsers.value = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        ElMessage.error('搜索用户失败');
        availableUsers.value = [];
      } finally {
        userSearchLoading.value = false;
      }
    };

    const handleSelectionChange = (selection) => {
      selectedUserIds.value = selection.map(u => u.id);
    };

    const openAddDialog = () => {
      selectedUserIds.value = [];
      availableUsers.value = [];
      userSearchQuery.value = '';
      addDialogVisible.value = true;
      searchUsers();
    };

    const confirmAdd = async () => {
      if (selectedUserIds.value.length === 0) return;
      try {
        if (selectedUserIds.value.length === 1) {
          await addClerk(selectedUserIds.value[0], []);
        } else {
          await batchAddClerks(selectedUserIds.value);
        }
        ElMessage.success('添加店员成功');
        addDialogVisible.value = false;
        fetchData();
      } catch (error) {
        ElMessage.error('添加店员失败');
      }
    };

    const handleRemove = async (row) => {
      try {
        await removeClerk(row.id);
        ElMessage.success('移除店员成功');
        fetchData();
      } catch (error) {
        ElMessage.error('移除店员失败');
      }
    };

    const openPermissionDialog = (row) => {
      permTargetUser.value = row;
      permSelected.value = row.permissions || [];
      permDialogVisible.value = true;
    };

    const confirmPermission = async () => {
      if (!permTargetUser.value) return;
      try {
        await updateClerkPermissions(permTargetUser.value.id, permSelected.value);
        ElMessage.success('权限更新成功');
        permDialogVisible.value = false;
        fetchData();
      } catch (error) {
        ElMessage.error('权限更新失败');
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchData();
    };

    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchData();
    };

    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchData();
    };

    const formatDate = (date) => {
      if (!date) return '-';
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    onMounted(() => fetchData());

    return {
      clerks, searchQuery, currentPage, pageSize, total, loading,
      addDialogVisible, userSearchQuery, availableUsers, userSearchLoading, selectedUserIds,
      permDialogVisible, permTargetUser, permSelected,
      handleSearch, handleSizeChange, handleCurrentChange,
      openAddDialog, searchUsers, handleSelectionChange, confirmAdd, handleRemove,
      openPermissionDialog, confirmPermission, formatDate
    };
  }
};
</script>

<style scoped>

.card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.filter-group { display: flex; align-items: center; }
.card-footer { margin-top: 20px; display: flex; justify-content: flex-end; }
.dialog-search { display: flex; align-items: center; }
.perm-section { margin-bottom: 16px; }
.perm-section-title { font-weight: 600; color: #333; margin-bottom: 8px; font-size: 14px; }
.perm-section .el-checkbox { margin-right: 16px; margin-bottom: 8px; }
</style>
